import * as THREE from "three";

export const ORBITAL_SPHERE_DEFAULTS = Object.freeze({
  speed: 1,
  particleSize: 0.015,
  particleOpacity: 0.8,
  orbitOpacity: 0.25,
  scale: 1,
  haloOpacity: 0.2,
  hue: 0,
});

const RADIUS = 2.2;
const PARTICLE_COUNT = 15000;
const ORBIT_COUNT = 6;
const ORBIT_SEGMENTS = 90;

function buildParticleGeometry() {
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const colors = new Float32Array(PARTICLE_COUNT * 3);

  // В оригинале цвет смешивался заново для каждой частицы,
  // но вариантов всего два — считаем их один раз.
  const dim = new THREE.Color(0x701a75);
  const bright = new THREE.Color(0xa78bfa);
  const strong = dim.clone().lerp(bright, 1);
  const weak = dim.clone().lerp(bright, 0.3);

  const spiral = Math.sqrt(PARTICLE_COUNT * Math.PI);
  let count = 0;

  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    const phi = Math.acos(-1 + (2 * i) / PARTICLE_COUNT);
    const theta = spiral * phi;
    const sinPhi = Math.sin(phi);
    const x = RADIUS * Math.cos(theta) * sinPhi;
    const y = RADIUS * Math.sin(theta) * sinPhi;
    const z = RADIUS * Math.cos(phi);

    const noise = Math.sin(x * 3.5) * Math.cos(y * 3.5) * Math.sin(z * 3.5) + Math.cos(x * 6) * 0.4;
    if (noise <= -0.1) continue;

    const distortion = 1 + noise * 0.1;
    const color = noise > 0.5 ? strong : weak;
    const o = count * 3;

    positions[o] = x * distortion;
    positions[o + 1] = y * distortion;
    positions[o + 2] = z * distortion;
    colors[o] = color.r;
    colors[o + 1] = color.g;
    colors[o + 2] = color.b;
    count += 1;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions.slice(0, count * 3), 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors.slice(0, count * 3), 3));
  return geometry;
}

function buildOrbitGeometry(orbitRadius) {
  const points = new Float32Array((ORBIT_SEGMENTS + 1) * 3);
  for (let p = 0; p <= ORBIT_SEGMENTS; p += 1) {
    const angle = (p / ORBIT_SEGMENTS) * Math.PI * 2;
    points[p * 3] = Math.cos(angle) * orbitRadius;
    points[p * 3 + 1] = Math.sin(angle) * orbitRadius;
    points[p * 3 + 2] = Math.sin(angle * 4) * 0.1;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(points, 3));
  return geometry;
}

/**
 * Низкоуровневый рендерер: только сцена, без цикла анимации.
 * @param {HTMLCanvasElement} canvas
 * @param {() => typeof ORBITAL_SPHERE_DEFAULTS} getOptions
 */
export function createOrbitalSphereRenderer(canvas, getOptions) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const group = new THREE.Group();
  scene.add(group);

  const particleGeometry = buildParticleGeometry();
  const particleMaterial = new THREE.PointsMaterial({
    size: 0.015,
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  group.add(new THREE.Points(particleGeometry, particleMaterial));

  const orbitMaterial = new THREE.LineBasicMaterial({
    color: 0x8b5cf6,
    transparent: true,
    opacity: 0.25,
    blending: THREE.AdditiveBlending,
  });

  // Узлы и ореолы одинаковые — одна геометрия и один материал на всех.
  const nodeGeometry = new THREE.SphereGeometry(0.025, 16, 16);
  const haloGeometry = new THREE.SphereGeometry(0.08, 16, 16);
  const nodeMaterial = new THREE.MeshBasicMaterial({ color: 0xd946ef });
  const haloMaterial = new THREE.MeshBasicMaterial({
    color: 0xc084fc,
    transparent: true,
    opacity: 0.2,
    blending: THREE.AdditiveBlending,
  });

  const orbitGeometries = [];
  const lines = [];

  for (let i = 0; i < ORBIT_COUNT; i += 1) {
    const orbitRadius = RADIUS * (1.08 + Math.random() * 0.2);
    const geometry = buildOrbitGeometry(orbitRadius);
    orbitGeometries.push(geometry);

    const line = new THREE.Line(geometry, orbitMaterial);
    line.rotation.x = Math.random() * Math.PI * 2;
    line.rotation.y = Math.random() * Math.PI * 2;
    group.add(line);
    lines.push(line);

    if (i % 2 !== 0) {
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
      const angle = Math.random() * Math.PI * 2;
      node.position.set(Math.cos(angle) * orbitRadius, Math.sin(angle) * orbitRadius, 0);
      node.add(new THREE.Mesh(haloGeometry, haloMaterial));
      line.add(node);
    }
  }

  let responsiveScale = 1;

  return {
    resize(width, height) {
      camera.aspect = width / Math.max(1, height);
      renderer.setSize(width, height, false);

      if (width >= 1024) {
        group.position.set(2.5, 0, -2);
        responsiveScale = 1.15;
        camera.position.z = 5.5;
      } else {
        group.position.set(0, -1, -3);
        responsiveScale = 1;
        camera.position.z = 6.5;
      }
      camera.updateProjectionMatrix();
    },

    /** @param {number} step — сколько «кадров по 60 fps» прошло (0 = просто перерисовать) */
    render(step = 1) {
      const o = getOptions();
      particleMaterial.size = o.particleSize;
      particleMaterial.opacity = o.particleOpacity;
      orbitMaterial.opacity = o.orbitOpacity;
      haloMaterial.opacity = o.haloOpacity;
      group.scale.setScalar(responsiveScale * o.scale);

      const s = o.speed * step;
      group.rotation.y += 0.0008 * s;
      group.rotation.x += 0.0003 * s;
      for (let i = 0; i < lines.length; i += 1) {
        lines[i].rotation.z += 0.0004 * s * (i % 2 === 0 ? -1 : 1);
      }

      renderer.render(scene, camera);
    },

    dispose() {
      particleGeometry.dispose();
      particleMaterial.dispose();
      orbitMaterial.dispose();
      orbitGeometries.forEach((g) => g.dispose());
      nodeGeometry.dispose();
      haloGeometry.dispose();
      nodeMaterial.dispose();
      haloMaterial.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
    },
  };
}

/**
 * Готовый фон: создаёт canvas внутри host, крутит анимацию,
 * сам ставит её на паузу вне экрана и в скрытой вкладке.
 * @param {HTMLElement} host
 * @param {Partial<typeof ORBITAL_SPHERE_DEFAULTS>} initialOptions
 */
export function mountOrbitalSphere(host, initialOptions = {}) {
  let options = { ...ORBITAL_SPHERE_DEFAULTS, ...initialOptions };

  host.classList.add("threeui-background", "orbital-sphere");
  const canvas = document.createElement("canvas");
  host.appendChild(canvas);

  const applyHue = () => {
    canvas.style.filter = options.hue ? `hue-rotate(${options.hue}deg)` : "";
  };
  applyHue();

  const sphere = createOrbitalSphereRenderer(canvas, () => options);

  let frame = 0;
  let last = 0;
  let visible = true;
  const canRun = () => visible && !document.hidden;

  const tick = (now) => {
    // Скорость не зависит от частоты экрана (60/120/144 Гц).
    const step = last ? Math.min((now - last) / (1000 / 60), 4) : 1;
    last = now;
    sphere.render(step);
    frame = canRun() ? requestAnimationFrame(tick) : 0;
  };

  const start = () => {
    if (frame || !canRun()) return;
    last = 0;
    frame = requestAnimationFrame(tick);
  };

  const stop = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };

  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    sphere.resize(width, height);
    sphere.render(0);
  };

  const resizeObserver = new ResizeObserver(resize);
  const intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry?.isIntersecting ?? true;
    if (visible) start();
    else stop();
  });
  // В оригинале после возврата во вкладку анимация не возобновлялась.
  const onVisibility = () => (document.hidden ? stop() : start());

  resizeObserver.observe(host);
  intersectionObserver.observe(host);
  document.addEventListener("visibilitychange", onVisibility);
  resize();
  start();

  return {
    update(next = {}) {
      options = { ...options, ...next };
      applyHue();
      if (!frame) sphere.render(0);
    },
    destroy() {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      sphere.dispose();
      canvas.remove();
    },
  };
}
